import Image from "next/image";
import Link from "next/link";
import { FaInstagram } from "react-icons/fa";

import { useStoreContext } from "../../contexts/StoreDataContext";
import { cn } from "../../lib/utils";
import { trackEvent } from "../../utils/analytics";
import { EVENTS } from "../../utils/analyticsConfig";
import { localLegalInfo } from "../utils/local.policy";

const SectionHeading = ({ label }) => {
  return <div className="font-bold">{label}</div>;
};

const LinkItem = ({ href, label }) => {
  return (
    <Link href={href}>
      <a className="m-0 text-sm opacity-100">{label}</a>
    </Link>
  );
};

const FeaturedFooter = () => {
  const context = useStoreContext();
  const storeData = context?.storeData;
  const theme = context?.themeData;

  const navigateToHome = () => {
    const url = `https://dm2buy.app.link/apps?utm_source=footer&utm_campaign=${storeData?.subdomain}`;
    trackEvent(EVENTS.FOOTER_CTA, {
      referrer: storeData?.subdomain,
    });
    window.open(url, "_blank").focus();
  };

  const navigateToInstagram = () => {
    const url = `https://www.instagram.com/${storeData?.instagramHandle}`;
    if (typeof window !== "undefined") {
      const newWindow = window.open(url, "_blank");
      if (newWindow) {
        newWindow.focus();
      }
    }
  };

  const legalInfo = storeData?.legalInfo || localLegalInfo;
  const terms = legalInfo?.terms;
  const contacts = legalInfo?.contacts;
  const returns = legalInfo?.returns;
  const about = legalInfo?.about;
  const policy = legalInfo?.policy;
  const care = legalInfo?.care;
  const size = legalInfo?.size;
  // const privacy = legalInfo?.privacy;
  // const isNoLegalDataAvailable =
  //   !terms && !contacts && !returns && !privacy && !about && !policy && !care && !size;

  const isPrimaryColorPresent = !!theme?.primaryColor;
  const isBackgroundColorPresent = !!theme?.backgroundColor;

  /**
   * Determines the footer background color CSS class based on theme configuration.
   *
   * Priority rules:
   * 1. If both background and primary colors exist: use theme colors
   * 2. If background color is white: use default gray
   * 3. If only background color exists: use theme color
   * 4. If only primary color exists: use secondary color
   * 5. Default fallback: use app gray
   *
   * @returns {string} CSS class name for the footer background
   * - "": Use theme colors
   * - "bg-appGray": Use default gray background
   * - "bg-secondary": Use secondary color
   */
  const getFooterBgColor = () => {
    if (
      theme?.backgroundColor?.toLowerCase() === "#ffffff" ||
      theme?.backgroundColor?.toLowerCase() === "#fff"
    ) {
      return isPrimaryColorPresent ? "bg-secondary" : "bg-appGray";
    }

    if (isBackgroundColorPresent && isPrimaryColorPresent) {
      return "";
    }

    if (isPrimaryColorPresent) {
      return "bg-secondary";
    }

    return "bg-appGray";
  };

  return (
    <footer
      className={cn(
        "mt-20 flex flex-col gap-y-5 px-5 pb-6 text-fontBackground",
        getFooterBgColor(),
      )}
    >
      {/* Quick Links */}
      <div className="flex flex-col gap-y-4 pt-4">
        <SectionHeading label="Quick Links" />

        {policy && <LinkItem href="/policy" label="Privacy Policy" />}
        {care && <LinkItem href="/care" label="Product Care" />}
        {size && <LinkItem href="/size" label="Size" />}
        {terms && <LinkItem href="/terms" label="Terms" />}
        {returns && <LinkItem href="/returns" label="Shipping & Return" />}
        {contacts && <LinkItem href="/contact" label="Contact Us" />}
        {about && <LinkItem href="/about" label="About" />}
      </div>

      {/* About Us */}
      <div className="flex flex-col gap-y-4">
        <SectionHeading label="About Us" />
        <p>{storeData?.storeBio}</p>
      </div>

      {/* Social */}
      <div className="flex flex-col gap-y-4">
        <SectionHeading label="Social" />

        <div>
          <FaInstagram
            size={24}
            color="var(--font-background)"
            className="cursor-pointer"
            onClick={navigateToInstagram}
          />
        </div>
      </div>

      {/* Secure Payments */}
      <div className="flex flex-col gap-y-4">
        <SectionHeading label="Secure Payments" />
        <div>
          <Image
            src="https://dm2buy-aqbqh9cwb5cwb9he.z02.azurefd.net/dm2buy/dm2buy_asset_allpg.svg"
            alt="Secure Payments Logo"
            // layout="fixed"
            className="w-[480px] max-w-[80vw]"
            width={480}
            height={60}
          />
        </div>
      </div>

      {/* Copyright */}
      <div className="text-center text-sm text-fontBackground opacity-70">
        <p>
          ©2025 by {storeData?.storeName}. Made with love on{" "}
          <span
            className={cn(
              "cursor-pointer font-semibold text-blue-600 underline",
              isBackgroundColorPresent && "text-fontBackground",
            )}
            // onClick={navigateToHome}
          >
            dm2buy
          </span>{" "}
          ✌🏼
        </p>
      </div>
    </footer>
  );
};

export default FeaturedFooter;
